import { useState } from "react";
import axios from "axios";
import { Send, Loader2, Terminal } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./common";
import { useLang, t } from "../i18n";

const FIELD =
  "w-full bg-panel border border-ink/15 rounded-xl px-5 py-4 text-sm text-ink placeholder:text-mutedink/60 focus:outline-none focus:border-crimson/60 transition-colors font-mono";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { lang } = useLang();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t(lang, "form_toast_ok"), {
        description: t(lang, "form_toast_ok_d"),
      });
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t(lang, "form_toast_err"), {
        description: t(lang, "form_toast_err_d"),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative bg-sand border-t border-ink/10 py-24 sm:py-32 overflow-hidden scanline" data-testid="section-contact-form">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-crimson mb-4 flex items-center gap-2">
              <Terminal size={14} /> {t(lang, "form_kicker")}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink max-w-lg">
              {t(lang, "form_title")}
            </h2>
            <p className="mt-6 text-mutedink text-base sm:text-lg leading-relaxed max-w-md">
              Research collaboration, election-data consulting, media commentary or an invited lecture —
              transmit it here and it lands directly at{" "}
              <span className="text-ink font-semibold">abhishekvrm02@gmail.com</span>.
            </p>
            <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink space-y-2">
              <p>▸ encryption: tls / transactional relay</p>
              <p>▸ response window: typically 48 hrs</p>
              <p>▸ timezone: ist (utc +5:30)</p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={submit} className="bg-void border border-ink/15 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="contact-form-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t(lang, "form_name")}
                  className={FIELD}
                />
                <input
                  data-testid="contact-form-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t(lang, "form_email")}
                  className={FIELD}
                />
              </div>
              <textarea
                data-testid="contact-form-message-input"
                required
                minLength={10}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder={t(lang, "form_message")}
                className={`${FIELD} resize-none`}
              />
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-3 bg-crimson text-bone font-mono text-xs uppercase tracking-[0.25em] px-7 py-4 rounded-xl hover:bg-saffron disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {sending ? t(lang, "form_sending") : t(lang, "form_submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
